/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
/*$(window).scroll(function(){
  var sticky = $('#reviveheader'),
      scroll = $(window).scrollTop();

  if (scroll >= 200) sticky.addClass('fixed');
  else sticky.removeClass('fixed');
});*/
document.addEventListener('DOMContentLoaded', function() {
      // Cache DOM elements
      //alert('test');
      const carousel = document.querySelector('.nm-reviews-carousel');
      const carouselContainer = document.querySelector('.nm-carousel-container');
      const items = document.querySelectorAll('.nm-review-item');
      const dotsContainer = document.querySelector('.nm-carousel-dots');
      
      // Carousel state
      let currentPosition = 0;
      let itemsPerView = getItemsPerView();
      let maxPosition = Math.ceil(items.length / itemsPerView) - 1;
      let isAnimating = false;
      let autoplayTimer;
      
      // Touch handling variables
      let touchStartX = 0;
      let touchEndX = 0;
      let touchThreshold = 50; // Minimum swipe distance
      let isDragging = false;
      let startTranslate = 0;
      let currentTranslate = 0;
      
      // Initialize carousel
      function initCarousel() {
        // Preload all images to prevent blank content
        preloadImages();
        createDots();
        updateCarousel(false);
        setupEventListeners();
        startAutoplay();
      }
      
      // Determine items per view based on screen width
      function getItemsPerView() {
        const width = window.innerWidth;
        if (width >= 768) return 4; // 4 items on desktop and tablet
        return 2; // 2 items on mobile
      }
      
      // Preload all images
      function preloadImages() {
        const imgElements = document.querySelectorAll('.nm-review-image');
        imgElements.forEach(img => {
          const src = img.getAttribute('src');
          if (src) {
            const preloadImage = new Image();
            preloadImage.src = src;
          }
        });
      }
      
      // Update carousel position
      function updateCarousel(animate = true) {
        if (isAnimating) return;
        
        // Calculate how many items to move
        const itemWidth = 100 / itemsPerView;
        const position = currentPosition * itemsPerView;
        const translateValue = Math.min(position * itemWidth, (items.length - itemsPerView) * itemWidth);
        
        carousel.style.transition = animate ? 'transform 0.5s ease' : 'none';
        carousel.style.transform = `translateX(-${translateValue}%)`;
        currentTranslate = -translateValue;
        
        updateDots();
        
        if (animate) {
          isAnimating = true;
          setTimeout(() => {
            isAnimating = false;
          }, 500);
        }
      }
      
      // Create navigation dots
      function createDots() {
        dotsContainer.innerHTML = '';
        const numberOfDots = maxPosition + 1;
        
        for (let i = 0; i < numberOfDots; i++) {
          const dot = document.createElement('div');
          dot.classList.add('nm-dot');
          if (i === 0) dot.classList.add('active');
          dot.setAttribute('aria-label', `Go to slide ${i+1}`);
          dot.addEventListener('click', () => {
            if (!isAnimating) {
              currentPosition = i;
              updateCarousel(true);
              resetAutoplay();
            }
          });
          dotsContainer.appendChild(dot);
        }
      }
      
      // Update active dot
      function updateDots() {
        const dots = document.querySelectorAll('.nm-dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('active', index === currentPosition);
        });
      }
      
      // Move carousel in specified direction
      function moveCarousel(direction) {
        if (isAnimating) return;
        
        let newPosition = currentPosition + direction;
        
        if (newPosition > maxPosition) {
          newPosition = 0;
        } else if (newPosition < 0) {
          newPosition = maxPosition;
        }
        
        currentPosition = newPosition;
        updateCarousel(true);
        resetAutoplay();
      }
      
      // Start autoplay
      function startAutoplay() {
        autoplayTimer = setInterval(() => moveCarousel(1), 5000);
      }
      
      // Reset autoplay
      function resetAutoplay() {
        clearInterval(autoplayTimer);
        startAutoplay();
      }
      
      // Stop autoplay
      function stopAutoplay() {
        clearInterval(autoplayTimer);
      }
      
      // Touch event handlers
      function handleTouchStart(e) {
        stopAutoplay();
        touchStartX = e.touches[0].clientX;
        isDragging = true;
        startTranslate = currentTranslate;
        
        // Remove transition during drag
        carousel.style.transition = 'none';
      }
      
      function handleTouchMove(e) {
        if (!isDragging) return;
        
        touchEndX = e.touches[0].clientX;
        const diff = touchEndX - touchStartX;
        
        // Calculate how much to move based on finger position
        const itemWidth = 100 / itemsPerView;
        const movePercent = (diff / carouselContainer.offsetWidth) * 100;
        
        // Apply the transform with the drag offset
        carousel.style.transform = `translateX(${startTranslate + movePercent}%)`;
      }
      
      function handleTouchEnd() {
        if (!isDragging) return;
        isDragging = false;
        
        // Re-enable transition
        carousel.style.transition = 'transform 0.5s ease';
        
        const swipeDistance = touchEndX - touchStartX;
        
        if (Math.abs(swipeDistance) >= touchThreshold) {
          // Determine direction based on swipe
          if (swipeDistance > 0) {
            // Swiped right
            moveCarousel(-1);
          } else {
            // Swiped left
            moveCarousel(1);
          }
        } else {
          // If the swipe wasn't large enough, snap back to current position
          updateCarousel(true);
        }
        
        startAutoplay();
      }
      
      // Set up event listeners
      function setupEventListeners() {
        // Mouse hover events
        carousel.addEventListener('mouseenter', stopAutoplay);
        carousel.addEventListener('mouseleave', startAutoplay);
        
        // Touch events
        carouselContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
        carouselContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
        carouselContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
        
        // Handle window resize
        window.addEventListener('resize', () => {
          const newItemsPerView = getItemsPerView();
          if (newItemsPerView !== itemsPerView) {
            itemsPerView = newItemsPerView;
            maxPosition = Math.ceil(items.length / itemsPerView) - 1;
            currentPosition = Math.min(currentPosition, maxPosition);
            createDots();
          }
          updateCarousel(false);
        });
      }
      
      // Initialize
      initCarousel();
    });
    document.addEventListener('DOMContentLoaded', function() {
            const track = document.querySelector('.tankha');
            const slides = document.querySelectorAll('.bottle-item');
            const dots = document.querySelectorAll('.ankha-dot');
            
            let currentSlide = 0;
            let slideCount = slides.length;
            let autoplayInterval;
            let isDragging = false;
            let startPos = 0;
            let currentTranslate = 0;
            let prevTranslate = 0;
            let isCarouselMode = false; // Flag to track if we're in carousel mode
            
            // Check if we're in carousel mode (mobile view)
            function checkCarouselMode() {
                isCarouselMode = window.innerWidth <= 768;
                
                // Apply or remove carousel specific styling
                if (isCarouselMode) {
                    track.style.transform = `translateX(-${calculateTranslation(currentSlide)}px)`;
                    document.querySelector('.ankha-dots').style.display = 'flex';
                } else {
                    // Reset transform in desktop mode
                    track.style.transform = 'translateX(0)';
                    document.querySelector('.ankha-dots').style.display = 'none';
                }
                
                // Refresh event listeners based on mode
                setupEventListeners();
            }
            
            // Set up the appropriate event listeners based on mode
            function setupEventListeners() {
                // Remove existing listeners to prevent duplicates
                track.removeEventListener('mousedown', dragStart);
                track.removeEventListener('mouseup', dragEnd);
                track.removeEventListener('mouseleave', dragEnd);
                track.removeEventListener('mousemove', drag);
                track.removeEventListener('touchstart', touchStart);
                track.removeEventListener('touchend', touchEnd);
                track.removeEventListener('touchmove', touchMove);
                
                // Only add carousel listeners in carousel mode
                if (isCarouselMode) {
                    // Mouse events for drag functionality
                    track.addEventListener('mousedown', dragStart);
                    track.addEventListener('mouseup', dragEnd);
                    track.addEventListener('mouseleave', dragEnd);
                    track.addEventListener('mousemove', drag);
                    
                    // Touch events for mobile swipe
                    track.addEventListener('touchstart', touchStart, {passive: true});
                    track.addEventListener('touchend', touchEnd, {passive: true});
                    track.addEventListener('touchmove', touchMove, {passive: false});
                    
                    // Start autoplay only in carousel mode
                    startAutoplay();
                } else {
                    // Stop autoplay in desktop mode
                    clearInterval(autoplayInterval);
                }
            }
            
            // Calculate how many slides are visible in the current viewport
            function getVisibleSlideCount() {
                if (!isCarouselMode) return slideCount; // All slides visible in desktop
                
                const containerWidth = document.querySelector('.pankha').offsetWidth;
                const slideWidth = slides[0].offsetWidth;
                const slideGap = 8; // Match the gap in CSS (reduced to 8px for mobile)
                return Math.floor(containerWidth / (slideWidth + slideGap));
            }
            
            // Update dots indicator
            function updateDots() {
                if (!isCarouselMode) return; // No dots in desktop mode
                
                dots.forEach((dot, index) => {
                    dot.classList.toggle('active', index === currentSlide);
                });
            }
            
            // Calculate the maximum allowed slide index
            function getMaxSlideIndex() {
                if (!isCarouselMode) return 0; // No scrolling in desktop mode
                
                // Get container and slide dimensions
                const containerWidth = document.querySelector('.pankha').offsetWidth;
                const slideWidth = slides[0].offsetWidth;
                
                // Use margin instead of gap for mobile
                const slideGap = window.innerWidth <= 480 ? 8 : 12; 
                
                // For mobile view, ensure we can see the last slide by using a more conservative calculation
                if (window.innerWidth <= 480) {
                    // Force max index to be exactly the last slide to ensure it's accessible
                    return slideCount - 1;
                }
                
                // For tablet and larger, use normal calculation
                let maxIndex = slideCount - Math.ceil(containerWidth / (slideWidth + slideGap));
                
                // Ensure maxIndex is at least slideCount - 1 to show the last slide
                maxIndex = Math.min(Math.max(maxIndex, 0), slideCount - 1);
                
                return maxIndex;
            }
            
            // Calculate the correct translation value with improved boundary handling
            function calculateTranslation(slideIndex) {
                if (!isCarouselMode) return 0; // No translation in desktop mode
                
                const slideWidth = slides[0].offsetWidth;
                const slideGap = window.innerWidth <= 480 ? 8 : 12; // Different gap for mobile
                
                // Ensure valid slide index
                slideIndex = Math.max(0, Math.min(slideIndex, getMaxSlideIndex()));
                
                // Special handling for last slide on mobile
                if (window.innerWidth <= 480 && slideIndex === slideCount - 1) {
                    // Get container width to ensure we don't show whitespace
                    const containerWidth = document.querySelector('.pankha').offsetWidth;
                    const totalWidth = slideCount * slideWidth + (slideCount - 1) * slideGap;
                    
                    // Calculate the maximum possible translation that won't show whitespace
                    return Math.min(slideIndex * (slideWidth + slideGap), totalWidth - containerWidth);
                }
                
                // Normal translation calculation
                return slideIndex * (slideWidth + slideGap);
            }
            
            // Go to slide with improved boundary handling
            function goToSlide(slideIndex) {
                if (!isCarouselMode) return; // No sliding in desktop mode
                
                // Ensure valid slide index with improved boundary checks
                const maxSlideIndex = getMaxSlideIndex();
                slideIndex = Math.max(0, Math.min(slideIndex, maxSlideIndex));
                
                currentSlide = slideIndex;
                
                // Calculate and apply transform with smooth transition
                const offset = calculateTranslation(slideIndex);
                track.style.transform = `translateX(-${offset}px)`;
                
                updateDots();
                
                // Reset autoplay timer
                resetAutoplay();
            }
            
            // Event listeners for dots with improved handling
            dots.forEach((dot, index) => {
                dot.addEventListener('click', () => {
                    if (isCarouselMode) {
                        goToSlide(index);
                    }
                });
            });
            
            function dragStart(e) {
                if (!isCarouselMode) return; // No dragging in desktop mode
                
                e.preventDefault();
                
                startPos = e.clientX;
                isDragging = true;
                
                // Get current translate position
                const transform = window.getComputedStyle(track).getPropertyValue('transform');
                if (transform !== 'none') {
                    const matrix = new DOMMatrixReadOnly(transform);
                    prevTranslate = matrix.m41;
                } else {
                    prevTranslate = 0;
                }
                
                // Pause autoplay during interaction
                clearInterval(autoplayInterval);
                
                // Change cursor while dragging
                track.style.cursor = 'grabbing';
            }
            
            // Separate touch handlers with improved mobile support
            function touchStart(e) {
                if (!isCarouselMode) return; // No touch handling in desktop mode
                
                startPos = e.touches[0].clientX;
                isDragging = true;
                
                // Get current translate position
                const transform = window.getComputedStyle(track).getPropertyValue('transform');
                if (transform !== 'none') {
                    const matrix = new DOMMatrixReadOnly(transform);
                    prevTranslate = matrix.m41;
                } else {
                    prevTranslate = 0;
                }
                
                // Pause autoplay during interaction
                clearInterval(autoplayInterval);
            }
            
            function drag(e) {
                if (!isDragging || !isCarouselMode) return;
                
                // Calculate how far we've dragged
                const currentPosition = e.clientX;
                const diff = currentPosition - startPos;
                
                // Apply the translation with improved boundaries
                let newTranslate = prevTranslate + diff;
                
                // Prevent dragging beyond boundaries with added resistance
                const maxTranslate = 0;
                const minTranslate = -calculateTranslation(getMaxSlideIndex());
                
                if (newTranslate > maxTranslate) {
                    // Add resistance when pulling beyond the start
                    newTranslate = maxTranslate + (diff * 0.2);
                } else if (newTranslate < minTranslate) {
                    // Add resistance when pulling beyond the end
                    newTranslate = minTranslate + ((diff + prevTranslate + minTranslate) * 0.2);
                }
                
                currentTranslate = newTranslate;
                track.style.transform = `translateX(${currentTranslate}px)`;
            }
            
            function touchMove(e) {
                if (!isDragging || !isCarouselMode) return;
                
                // Prevent default behavior to stop page scrolling
                e.preventDefault();
                
                // Calculate drag distance
                const currentPosition = e.touches[0].clientX;
                const diff = currentPosition - startPos;
                
                // Apply translation with improved boundaries
                let newTranslate = prevTranslate + diff;
                
                // Prevent dragging beyond boundaries with added resistance
                const maxTranslate = 0;
                const minTranslate = -calculateTranslation(getMaxSlideIndex());
                
                if (newTranslate > maxTranslate) {
                    // Add resistance when pulling beyond the start
                    newTranslate = maxTranslate + (diff * 0.2);
                } else if (newTranslate < minTranslate) {
                    // Add resistance when pulling beyond the end
                    newTranslate = minTranslate + ((diff + prevTranslate + minTranslate) * 0.2);
                }
                
                currentTranslate = newTranslate;
                track.style.transform = `translateX(${currentTranslate}px)`;
            }
            
            function dragEnd() {
                if (!isDragging || !isCarouselMode) return;
                
                isDragging = false;
                
                // Reset cursor
                track.style.cursor = 'grab';
                
                finalizeDrag();
            }
            
            function touchEnd() {
                if (!isDragging || !isCarouselMode) return;
                
                isDragging = false;
                
                finalizeDrag();
            }
            
            function finalizeDrag() {
                if (!isCarouselMode) return; // No drag in desktop mode
                
                // Calculate which slide to snap to based on drag distance
                const slideWidth = slides[0].offsetWidth + (window.innerWidth <= 480 ? 8 : 12);
                let draggedSlides = Math.round(-currentTranslate / slideWidth);
                
                // Get the max valid slide index
                const maxSlideIndex = getMaxSlideIndex();
                
                // Ensure valid destination and animate to it
                if (draggedSlides < 0) {
                    // Snap back to first slide if pulled beyond start
                    goToSlide(0);
                } else if (draggedSlides > maxSlideIndex) {
                    // Snap to last valid slide if pulled beyond end
                    goToSlide(maxSlideIndex);
                } else {
                    // Normal slide transition
                    goToSlide(draggedSlides);
                }
                
                // Resume autoplay
                startAutoplay();
            }
            
            // Pause autoplay on hover or focus
            const carouselContainer = document.querySelector('.ankha');
            
            carouselContainer.addEventListener('mouseenter', () => {
                if (isCarouselMode) {
                    clearInterval(autoplayInterval);
                }
            });
            
            carouselContainer.addEventListener('mouseleave', () => {
                if (isCarouselMode && !isDragging) {
                    startAutoplay();
                }
            });
            
            carouselContainer.addEventListener('focusin', () => {
                if (isCarouselMode) {
                    clearInterval(autoplayInterval);
                }
            });
            
            carouselContainer.addEventListener('focusout', () => {
                if (isCarouselMode && !isDragging) {
                    startAutoplay();
                }
            });
            
            // Improved autoplay with proper handling of the last slide
            function startAutoplay() {
                if (!isCarouselMode) return; // No autoplay in desktop mode
                
                clearInterval(autoplayInterval);
                autoplayInterval = setInterval(() => {
                    // Get the max valid slide index
                    const maxSlideIndex = getMaxSlideIndex();
                    
                    // Check if we're at the last slide
                    if (currentSlide >= maxSlideIndex) {
                        // Start again from the first slide with smooth transition
                        animateToSlide(0);
                    } else {
                        // Move to next slide
                        animateToSlide(currentSlide + 1);
                    }
                }, 5000); // Change slide every 5 seconds
            }
            
            // Improved smooth animation between slides
            function animateToSlide(slideIndex) {
                if (!isCarouselMode) return;
                
                // Ensure transition is smooth
                track.style.transition = 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)';
                
                // Go to the target slide
                goToSlide(slideIndex);
            }
            
            // Reset autoplay with improved timing
            function resetAutoplay() {
                if (!isCarouselMode) return; // No autoplay in desktop mode
                
                clearInterval(autoplayInterval);
                // Add small delay before starting autoplay again
                setTimeout(() => {
                    startAutoplay();
                }, 100);
            }
            
            // Handle window resize with improved recalculation
            function handleResize() {
                // Save current slide for reference
                const currentSlideBeforeResize = currentSlide;
                
                // Recheck carousel mode
                checkCarouselMode();
                
                if (isCarouselMode) {
                    // Get new max slide index after resize
                    const maxSlideIndex = getMaxSlideIndex();
                    
                    // If current slide is beyond max after resize, adjust it
                    if (currentSlideBeforeResize > maxSlideIndex) {
                        currentSlide = maxSlideIndex;
                    } else {
                        currentSlide = currentSlideBeforeResize;
                    }
                    
                    // Apply new position without animation
                    track.style.transition = 'none';
                    track.style.transform = `translateX(-${calculateTranslation(currentSlide)}px)`;
                    
                    // Force browser to apply the non-animated transform first
                    track.offsetHeight;
                    
                    // Restore transition for future slides
                    setTimeout(() => {
                        track.style.transition = 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)';
                    }, 50);
                    
                    // Update dots
                    updateDots();
                }
            }
            
            // Initial setup
            function initialSetup() {
                // Check carousel mode and set initial position
                checkCarouselMode();
                
                // Force immediate update of visible slides
                if (isCarouselMode) {
                    track.style.transition = 'none';
                    goToSlide(0);
                    
                    // Restore transition for future slides
                    setTimeout(() => {
                        track.style.transition = 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)';
                    }, 50);
                }
            }
            
            // Run initial setup
            initialSetup();
            
            // Handle window resize events with debounce
            let resizeTimer;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(handleResize, 250);
            });
            
            // Ensure proper layout calculation after images load
            window.addEventListener('load', () => {
                // Short delay to ensure all layout calculations are complete
                setTimeout(handleResize, 100);
            });
        });
document.addEventListener('DOMContentLoaded', function() {
  const slider = document.querySelector('.pnnet-slider');
  const carouselWrap = document.querySelector('.nnet-carousel-wrap');
  const slides = document.querySelectorAll('.sone-slide');
  const dotsWrap = document.querySelector('.pnnet-dots');
  let currentPos = 0;
  let slidesPerView = getSlidesPerView();
  let maxPos = Math.max(0, slides.length - slidesPerView);
  let isMoving = false;
  
  // Touch handling variables
  let touchStartX = 0;
  let touchEndX = 0;
  let touchThreshold = 50; // Minimum swipe distance in pixels
  let isDragging = false;
  let startTranslate = 0;
  let currentTranslate = 0;
  let autoplayTimer;

  function getSlidesPerView() {
    const width = window.innerWidth;
    if (width >= 1200) return 5;
    if (width >= 1024) return 4;
    if (width >= 768) return 3;
    return 2; // Changed to always return at least 2 for mobile
  }

  function updateSlider(animate = true) {
    if (isMoving) return;
    
    const moveWidth = 100 / slidesPerView;
    if (animate) {
      isMoving = true;
      slider.style.transition = 'transform 0.5s ease-in-out';
    } else {
      slider.style.transition = 'none';
    }
    
    slider.style.transform = `translateX(-${currentPos * moveWidth}%)`;
    currentTranslate = -currentPos * moveWidth;
    updateDots();
    
    if (animate) {
      setTimeout(() => {
        isMoving = false;
      }, 500);
    }
  }

  function createDots() {
    dotsWrap.innerHTML = '';
    const dotCount = Math.min(maxPos + 1, Math.ceil(slides.length / slidesPerView));
    
    for (let i = 0; i < dotCount; i++) {
      const dot = document.createElement('div');
      dot.classList.add('sone-dot');
      if (i === 0) dot.classList.add('nnet-active');
      dot.addEventListener('click', () => {
        if (!isMoving) goToSlide(i);
      });
      dotsWrap.appendChild(dot);
    }
  }

  function updateDots() {
    const dots = document.querySelectorAll('.sone-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('nnet-active', index === currentPos);
    });
  }

  function moveSlider(direction) {
    if (isMoving) return;
    
    let newPos = currentPos + direction;
    
    if (newPos > maxPos) {
      newPos = 0;
    } else if (newPos < 0) {
      newPos = maxPos;
    }
    
    currentPos = newPos;
    updateSlider();
    resetAutoplay();
  }

  function goToSlide(position) {
    if (position >= 0 && position <= maxPos && !isMoving) {
      currentPos = position;
      updateSlider();
      resetAutoplay();
    }
  }

  // Touch event handlers
  function handleTouchStart(e) {
    stopAutoplay();
    touchStartX = e.touches[0].clientX;
    isDragging = true;
    startTranslate = currentTranslate;
    
    // Remove transition during drag
    slider.style.transition = 'none';
  }
  
  function handleTouchMove(e) {
    if (!isDragging) return;
    
    touchEndX = e.touches[0].clientX;
    const diff = touchEndX - touchStartX;
    
    // Calculate how much to move based on finger position
    const moveWidth = 100 / slidesPerView;
    const movePercent = (diff / carouselWrap.offsetWidth) * 100;
    
    // Apply the transform with the drag offset
    slider.style.transform = `translateX(${startTranslate + movePercent}%)`;
  }
  
  function handleTouchEnd() {
    if (!isDragging) return;
    isDragging = false;
    
    // Re-enable transition
    slider.style.transition = 'transform 0.5s ease-in-out';
    
    const swipeDistance = touchEndX - touchStartX;
    
    if (Math.abs(swipeDistance) >= touchThreshold) {
      // Determine direction based on swipe
      if (swipeDistance > 0) {
        // Swiped right (previous)
        moveSlider(-1);
      } else {
        // Swiped left (next)
        moveSlider(1);
      }
    } else {
      // If the swipe wasn't large enough, snap back to current position
      updateSlider(true);
    }
    
    // Removed startAutoplay() call
  }
  
  // Autoplay functions (disabled)
  function startAutoplay() {
    // Autoplay disabled
  }
  
  function resetAutoplay() {
    // Autoplay disabled
  }
  
  function stopAutoplay() {
    // Autoplay disabled
  }

  // Initialize slider
  function initSlider() {
    createDots();
    updateSlider(false);
    setupEventListeners();
    // Removed startAutoplay() call
  }
  
  // Setup all event listeners
  function setupEventListeners() {
    // Make goToSlide function globally available for dots
    window.goToSlide = goToSlide;
    
    // Touch events
    carouselWrap.addEventListener('touchstart', handleTouchStart, { passive: true });
    carouselWrap.addEventListener('touchmove', handleTouchMove, { passive: true });
    carouselWrap.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    // Removed hover event listeners for autoplay
    
    // Handle window resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const newSlidesPerView = getSlidesPerView();
        if (newSlidesPerView !== slidesPerView) {
          slidesPerView = newSlidesPerView;
          maxPos = Math.max(0, slides.length - slidesPerView);
          currentPos = Math.min(currentPos, maxPos);
          createDots();
          updateSlider(false);
        }
      }, 200);
    });
  }
  
  // Initialize the slider
  initSlider();
});